// Carga inicial de stock — reglas de pantalla, sin Vue ni red
// (pos-graciela-api/docs/SPEC_CARGA_INICIAL_STOCK_Y_PRECIOS_v0.1.md).

import { avisoCantidad } from './presentacionCompra.mjs';

export const ORIGEN_CARGA_INICIAL = 'CARGA_INICIAL';

// RH-3: cargar compra, crear producto y cargar precio de venta.
export const PERMISOS_CARGA = Object.freeze(['crear_factura_compra', 'crear_productos', 'crear_precio_venta']);
// §6.8: editar una carga inicial confirmada.
export const PERMISO_EDICION = 'editar_factura_compra';

/** RH-1/RH-3: el switch encendido y los tres permisos. */
export function puedeUsarCargaInicial({ habilitada, permisos = [] } = {}) {
  return habilitada === true && PERMISOS_CARGA.every((p) => permisos.includes(p));
}

/** §6.8: el switch encendido y el permiso de edición. */
export function puedeEditarCargaInicial({ habilitada, permisos = [] } = {}) {
  return habilitada === true && permisos.includes(PERMISO_EDICION);
}

const numero = (valor) => {
  if (valor === null || valor === undefined || valor === '') return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
};

/**
 * Margen sobre el costo, en % con un decimal: (venta − compra) / compra.
 * Null si no hay costo (0 o vacío) o no hay precio de venta. Sólo informativo.
 */
export function margenSobreCosto(costo, venta) {
  const c = numero(costo);
  const v = numero(venta);
  if (!c || c <= 0 || v === null || v <= 0) return null;
  return Math.round(((v - c) / c) * 1000) / 10;
}

/** «Aplicar X %»: precio de venta al guaraní entero. */
export function precioConMargen(costo, porcentaje) {
  const c = numero(costo);
  const p = numero(porcentaje);
  if (!c || c <= 0 || p === null) return null;
  return Math.round(c * (1 + p / 100));
}

/**
 * Lee la respuesta de GET /api/prices/:producto_id (200, o el cuerpo del 404
 * «sin precio») y decide qué muestra la fila: precio actual y si la celda de
 * precio nuevo se puede editar (SPEC §6.4).
 */
export function estadoPrecio(info = {}, { sinPrecio = false } = {}) {
  const vigentes = Array.isArray(info.precios_vigentes) ? info.precios_vigentes : [];
  const programados = Array.isArray(info.precios_programados) ? info.precios_programados : [];
  const grupo = info.grupo_precio || null;
  const precioActual = sinPrecio
    ? null
    : numero(info.precio_unitario_resuelto) ?? numero(vigentes[0]?.precio_venta);

  let aviso = null;
  if (grupo) {
    aviso = `Precio por grupo: ${grupo.nombre}`;
  } else if (vigentes.length > 1) {
    aviso = `Tiene ${vigentes.length} tramos: el precio se edita en Precios`;
  } else if (programados.length) {
    aviso = `Tiene un precio programado desde el ${formatearFecha(programados[0].vigencia_desde)}`;
  }

  return {
    precioActual,
    tienePrecio: !sinPrecio && vigentes.length > 0,
    esGranel: info.es_granel === true,
    celdaHabilitada: aviso === null,
    aviso
  };
}

function formatearFecha(valor) {
  const texto = String(valor || '').slice(0, 10);
  const [, mes, dia] = texto.split("-");
  return dia && mes ? `${dia}/${mes}` : texto;
}

/** RT-3: qué le falta a la línea en curso para poder agregarse. */
export function faltantesLinea(linea = {}) {
  const faltan = [];
  if (!linea.producto_id) faltan.push('producto');
  const cantidad = numero(linea.cantidad);
  if (cantidad === null || cantidad <= 0) faltan.push('cantidad');
  // RF-12 (H-4): por caja, la cantidad puede ser decimal si da unidades
  // enteras (0,5 bolsa × 5000 = 2.500 u.); por unidad, entera como siempre.
  else if (linea.presentacion ? avisoCantidad(cantidad, linea.presentacion) : !Number.isInteger(cantidad)) {
    faltan.push(linea.presentacion ? 'una cantidad que dé unidades enteras' : 'cantidad entera');
  }
  const costo = numero(linea.precio_compra);
  if (costo !== null && costo < 0) faltan.push('precio de compra no negativo');
  return faltan;
}

/** Precio nuevo que se va a enviar, o null (vacío, 0 o celda deshabilitada). */
export function precioNuevoAEnviar(linea = {}) {
  if (linea.celdaHabilitada === false) return null;
  const p = numero(linea.precio_venta_nuevo);
  return p !== null && p > 0 ? p : null;
}

/** El payload de POST /api/purchases/invoices (mismo formato que Factura.vue + origen). */
export function armarPayloadCarga({ cabecera = {}, lineas = [] } = {}) {
  const total = lineas.reduce((acc, l) => acc + Number(l.cantidad || 0) * Number(l.precio_compra || 0), 0);
  return {
    cabecera: {
      origen: ORIGEN_CARGA_INICIAL,
      nro_comprobante: String(cabecera.nro_comprobante || '').trim(),
      timbrado: String(cabecera.timbrado || '').trim(),
      fecha_emision: cabecera.fecha_emision || null,
      tipo_moneda: 'PYG',
      credito_contado: 'CONTADO',
      tipo_documento: 'RUC',
      nro_documento: cabecera.nro_documento,
      nombre_razon_social: cabecera.nombre_razon_social,
      direccion: '',
      pendiente: false,
      total_iva: 0,
      total_iva_incluido: total,
      total_sin_iva: total
    },
    detalles: lineas.map(detalleParaApi)
  };
}

export function detalleParaApi(linea) {
  return {
    // Caja, bolsa, bulto: el API la convierte a unidades del producto.
    ...(linea.presentacion
      ? { presentacion_id: Number(linea.presentacion.presentacion_id) }
      : { producto_id: Number(linea.producto_id) }),
    cantidad: Number(linea.cantidad),
    precio_unitario_bruto: Number(linea.precio_compra || 0),
    descuento: 0,
    tipo_iva: Number(linea.tipo_iva_id || 1),
    fecha_vencimiento: linea.fecha_vencimiento || null
  };
}

/**
 * RP-2: por cada línea con precio nuevo, qué llamada de precios corresponde.
 * Sin precio → crear el tramo «desde 1»; con un tramo → programarlo hoy.
 */
export function planDePrecios(lineas = [], hoy) {
  return lineas
    .map((linea) => ({ linea, precio: precioNuevoAEnviar(linea) }))
    .filter(({ precio }) => precio !== null)
    .map(({ linea, precio }) => ({
      producto_id: Number(linea.producto_id),
      nombre: linea.nombre,
      precio,
      accion: linea.tienePrecio ? 'programar' : 'crear',
      body: linea.tienePrecio
        ? { cantidad_desde: 1, vigencia_desde: hoy, precio_venta: precio }
        : { cantidad_desde: 1, precio_venta: precio }
    }));
}

/** RP-5: cuántas líneas tienen costo 0. */
export function lineasConCostoCero(lineas = []) {
  return lineas.filter((l) => !(Number(l.precio_compra) > 0)).length;
}

/** §6.7: clave del borrador, por usuario y por instancia. */
export function claveBorrador(usuarioId, origin) {
  return `carga-inicial:${usuarioId || 'anon'}:${origin || ''}`;
}
